import { useEffect, useRef, useState } from 'react'
import { useGuideReveal } from '../guide/guideRegistry'

/**
 * Mount-on-first-show kit: tabs and collapsed sections render their content
 * only once opened, so a page never fetches data for what it is not showing.
 *
 * `keep` picks the unmount policy, and defaults to keeping state:
 * - `"visited"` mounts on the first open and stays mounted afterwards, so
 *   switching tabs keeps unsaved edits (the visited-set pattern from the
 *   ISO 27001 page). This is the default.
 * - `"active"` unmounts again when the surface closes. Use it only for
 *   content that is cheap to rebuild and holds no draft state.
 */

const NO_REVEALS = {}

/**
 * Headless mount decision: true once `active` has been true with
 * keep="visited", exactly while `active` with keep="active".
 */
export function useLazyMount(active, keep = 'visited') {
  const [visited, setVisited] = useState(() => Boolean(active))
  useEffect(() => {
    // Bail out when already visited (same value → no re-render), mirroring
    // the visitedTabs.setVisitedTabs update in ISO27001CompliancePage.
    if (active) setVisited((was) => was || active)
  }, [active])
  if (keep === 'active') return Boolean(active)
  return visited || Boolean(active)
}

/** Renders `children` only when useLazyMount says to. */
export function LazyMount({ active, keep = 'visited', children }) {
  const mount = useLazyMount(active, keep)
  return mount ? <>{children}</> : null
}

/**
 * A tabpanel whose content mounts on first activation. The panel element
 * itself always renders (so tab semantics and layout hold) and hides with
 * the `hidden` attribute while inactive.
 */
export function LazyTabPane({ active, keep = 'visited', id, labelledBy, children, ...rest }) {
  const mount = useLazyMount(active, keep)
  return (
    <div role="tabpanel" id={id} aria-labelledby={labelledBy} hidden={!active} {...rest}>
      {mount ? children : null}
    </div>
  )
}

/**
 * A native disclosure whose content mounts on first open.
 *
 * Uncontrolled by default (the browser owns the open state); `defaultOpen`
 * sets its initial state. Pass `open` with `onToggle` to control it.
 * `onToggle` receives the new open boolean.
 * `guideReveal` is the useGuideReveal map this section lends the tour engine
 * while mounted — pass a stable object.
 */
export function LazyDetails({
  summary,
  keep = 'visited',
  open,
  defaultOpen = false,
  onToggle,
  guideReveal,
  children,
  ...rest
}) {
  const controlled = open !== undefined
  const [uncontrolledOpen, setUncontrolledOpen] = useState(() => Boolean(open) || Boolean(defaultOpen))
  const isOpen = controlled ? Boolean(open) : uncontrolledOpen
  const mount = useLazyMount(isOpen, keep)
  const detailsRef = useRef(null)
  useGuideReveal(guideReveal || NO_REVEALS)

  useEffect(() => {
    const el = detailsRef.current
    if (!el) return undefined
    // A native listener rather than React's onToggle: it fires for opens the
    // browser performs itself (uncontrolled clicks) as well as scripted ones.
    const handleToggle = () => {
      if (!controlled) setUncontrolledOpen(el.open)
      onToggle?.(el.open)
    }
    el.addEventListener('toggle', handleToggle)
    return () => el.removeEventListener('toggle', handleToggle)
  }, [controlled, onToggle])

  return (
    // The attribute mirrors state in both modes: controlled from the prop,
    // uncontrolled from the initial `defaultOpen` and every native toggle.
    <details {...rest} ref={detailsRef} open={isOpen}>
      <summary>{summary}</summary>
      {mount ? children : null}
    </details>
  )
}
